/**
 * La connexion au back-office, en **deux appels** — `POST
 * /api/backoffice/session/challenge` puis `POST /api/backoffice/session`.
 *
 * **Le serveur est livré et ce fichier ne fait que l'appeler.** Le contrat est
 * celui de `BackOfficeApi` et de `BackOfficeMessages`.
 *
 * **Pourquoi deux appels, et pourquoi dans cet ordre.** Le second facteur du
 * back-office est un code envoyé par SMS depuis le chantier B8 : la possession
 * du téléphone, et non un second secret que l'agent noterait à côté du premier.
 * Le premier appel présente l'identifiant et le mot de passe ; **lui seul**
 * peut faire partir un SMS, et seulement s'ils sont justes. Le second présente
 * le **billet** rendu par le premier et le code reçu.
 *
 * **Le billet, et non le nom d'utilisateur.** C'est lui qui relie les deux
 * temps. Sans lui, qui connaît un identifiant et devine un code entrerait sans
 * mot de passe : les deux facteurs cesseraient d'être exigés ensemble. Il est
 * un secret au même titre qu'un jeton, et ne quitte jamais la mémoire du
 * processus.
 *
 * **Les refus que le produit confond** (`EX-BO-001`, `RG-06`) : identifiants
 * faux, mot de passe faux, refus en amont, billet inconnu ou expiré, code
 * faux, expiré ou rejoué. Tous ne portent pas le même **code** —
 * l'authentification donne `401`, et un refus rendu *avant* que la connexion
 * ne soit tentée, par le mandataire par exemple, donne `403`. Ce qui les rend
 * indiscernables, c'est que le client HTTP range les deux codes sous la même
 * nature d'erreur, `unauthorized` ; l'écran n'a donc jamais de quoi les
 * distinguer, même s'il le voulait.
 *
 * C'est aussi pourquoi ce module **ne lit pas le corps du refus**. Le corps
 * porte un texte différent selon la cause ; l'afficher rendrait à un attaquant
 * exactement ce que l'uniformité lui refuse.
 */

import type { ApiClient } from './api-client.js';
import type { OperatorSession } from '../state/session-store.js';

/**
 * L'en-tête d'authentification, et le schéma que `BackOfficeApi` attend.
 *
 * **Posé au point d'extension du client HTTP**, jamais dans chaque appel métier.
 * `api-client.ts` annonçait déjà l'endroit — « c'est ici que la story de
 * durcissement posera l'en-tête d'authentification » —, et le premier écran qui
 * appelle une route protégée est celui qui l'y pose. Le répéter dans chaque
 * fonction d'appel aurait fait de l'authentification une discipline : le premier
 * appel écrit sans elle serait parti nu, et aurait rendu 401 sans qu'on sache
 * pourquoi.
 */
export const authorizationHeader = 'Authorization';

/** Le schéma porteur, tel que le serveur le lit. */
export const bearerScheme = 'Bearer ';

/** Pose le jeton sur [headers], ou ne pose rien s'il n'y en a pas. */
export function attachBearerToken(headers: Headers, token: string | null | undefined): void {
  if (token === null || token === undefined || token === '') return;
  headers.set(authorizationHeader, `${bearerScheme}${token}`);
}

/** Le chemin du premier temps. */
export const backOfficeChallengePath = '/api/backoffice/session/challenge';

/** Le chemin du second temps. */
export const backOfficeSessionPath = '/api/backoffice/session';

/**
 * Le chemin de l'échange de jetons (dette `D-30`).
 *
 * **Il ne porte pas d'en-tête d'authentification, et ce n'est pas un oubli.** Le jeton
 * qui compte ici est celui d'*échange*, et il voyage dans le corps ; l'en-tête, lui,
 * porte le jeton d'*accès*, qui est précisément celui qui vient de mourir. Le serveur
 * ne lit pas l'en-tête sur cette route.
 */
export const backOfficeRefreshPath = '/api/backoffice/session/refresh';

/** Ce que le premier temps recueille. */
export interface BackOfficeCredentials {
  readonly username: string;
  readonly password: string;
}

/**
 * Ce que le premier temps rend.
 *
 * `delivery` dit **une seule chose** : l'envoi a-t-il été *demandé à la
 * passerelle*. Ce n'est pas « le SMS est arrivé » — la réponse immédiate de
 * l'opérateur ne distingue pas un envoi accepté d'un refus d'authentification
 * (`D-18`, mesuré le 2026-09-07). `DEGRADED` existe pour qu'un écran
 * n'attende jamais un code qui n'est jamais parti.
 */
export interface BackOfficeChallenge {
  /** Le billet à présenter au second temps. **Mémoire seulement.** */
  readonly ticket: string;
  /** `DELIVERED` ou `DEGRADED`. */
  readonly delivery: 'DELIVERED' | 'DEGRADED';
  /** Le nombre de chiffres du code, tel que la configuration le fixe. */
  readonly codeLength: number;
  /** La durée de validité du code, en secondes. */
  readonly expiresInSeconds: number;
  /** Le nombre d'essais tolérés sur ce code. */
  readonly maxAttempts: number;
}

/** Ce que le second temps recueille. */
export interface BackOfficeSecondFactor {
  readonly ticket: string;
  readonly code: string;
}

/**
 * Premier temps : soumet l'identifiant et le mot de passe.
 *
 * Aucune capture d'erreur ici : l'`ApiError` du client porte déjà sa nature et
 * sa clé de message, et l'avaler pour rendre `null` ferait perdre la
 * distinction entre « refusé » et « serveur injoignable ». L'appelant décide
 * quoi afficher.
 */
export async function openBackOfficeChallenge(
  client: ApiClient,
  credentials: BackOfficeCredentials,
): Promise<BackOfficeChallenge> {
  return client.request<BackOfficeChallenge>(backOfficeChallengePath, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      username: credentials.username,
      password: credentials.password,
    }),
  });
}

/** Second temps : présente le billet et le code, et ouvre la session. */
export async function completeBackOfficeSession(
  client: ApiClient,
  secondFactor: BackOfficeSecondFactor,
): Promise<OperatorSession> {
  return client.request<OperatorSession>(backOfficeSessionPath, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ticket: secondFactor.ticket,
      code: secondFactor.code,
    }),
  });
}

/**
 * Échange le jeton de rafraîchissement contre un couple neuf.
 *
 * **Un seul appel, jamais deux en parallèle.** Le serveur traite un jeton d'échange
 * présenté deux fois comme un vol : il fait tomber TOUTES les sessions de l'agent et
 * écrit une alerte. Deux requêtes qui se croiseraient — deux onglets, deux appels
 * simultanés qui reçoivent 401 en même temps — produiraient exactement ce rejeu, et
 * déconnecteraient l'agent en criant au vol sur son propre travail. C'est
 * `session-renewal.ts` qui garantit l'unicité ; cette fonction ne fait que
 * transporter.
 *
 * Aucune capture d'erreur : l'`ApiError` porte déjà sa nature, et l'avaler pour rendre
 * `null` ferait confondre « la session est finie » avec « le serveur est injoignable ».
 */
export async function refreshBackOfficeSession(
  client: ApiClient,
  refreshToken: string,
): Promise<OperatorSession> {
  return client.request<OperatorSession>(backOfficeRefreshPath, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });
}
